import type { GameState } from "@fiveout/core";
import {
  aValue,
  buildStacks,
  draws,
  showing,
  TUTORIAL_OPPONENT_ID,
  TUTORIAL_OPPONENT_NAME,
  TUTORIAL_PLAYER_ID,
  TUTORIAL_PLAYER_NAME,
  tutorialPlayer,
  tutorialRound,
  type Tutorial,
  type TutorialStep,
} from "./script";
import { BASICS_DECK_CONFIG } from "./basics";

/**
 * Tutorial – House Rules: Jourdan's Streaks and Constellations played out, and
 * a line each for Mercy, Drought and Burning Deck.
 *
 * Against Sam, because a streak is spent ON somebody and its locks land on
 * their turn. The player starts with their deck already burning (five clean
 * turns is not something to sit through in a tutorial). Constellations is on
 * with one star already lit and the event deck stacked with a Meteor Strike, so
 * the triplet and the staircase the script builds fill the meter on the
 * player's third turn and fire exactly that event.
 *
 * Mercy, Drought and Burning Deck are left off: each needs several turns (of
 * somebody falling behind, or of a fire left burning) to show, so the closing
 * card only describes them.
 *
 * Indices are 0 center, 1 up, 2 down, 3 left, 4 right; piles are top-first.
 * The heights are all different so "the two smallest piles" a streak locks is
 * never a tie: after the player's first turn they are the bottom (1) and left (3).
 */
const BOARD: number[][] = [
  [9, 3, 12, 6], // 0 center (4) — one of the 9s
  [9, 5, 1, 13, 7], // 1 up (5) — the other 9, covered by the 6 that makes four in a row
  [11], // 2 down (1) — locked, then the third 9
  [4, 10], // 3 left (2 -> 3) — locked
  [2, 8, 12, 4, 1, 10], // 4 right (6) — where Sam plays round the locks, then lays the 8
];

const FILLER = [8, 2, 10, 5, 13, 1, 7, 11, 3, 6, 12, 4, 10, 2, 8, 5, 1, 13, 6, 9];
const SAM_FILLER = [6, 11, 3, 8, 1, 12, 5, 9, 2, 13, 7, 4];

const SAM = TUTORIAL_OPPONENT_ID;

function initialState(): GameState {
  return tutorialRound({
    id: "tutorial-house-rules",
    deckConfig: BASICS_DECK_CONFIG,
    houseRules: { jourdansStreaks: true, starfall: true },
    stacks: buildStacks("thr", BOARD),
    players: [
      // Five clean turns already behind them: the deck is alight.
      tutorialPlayer(TUTORIAL_PLAYER_ID, TUTORIAL_PLAYER_NAME, draws("thr-deck", FILLER), { streak: 5, turnsTaken: 5 }),
      tutorialPlayer(SAM, TUTORIAL_OPPONENT_NAME, draws("thr-sam-deck", SAM_FILLER), { turnsTaken: 5 }),
    ],
    starfall: { stars: 1, deck: ["meteors"], rng: 20261015 },
  });
}

const STEPS: TutorialStep[] = [
  {
    id: "welcome",
    title: "House rules",
    prompt:
      "**House rules** are extra rules on top of the basics. A new game starts with all of them on.\n\n" +
      "This round shows the two you'll see most. You're playing against **Sam**.",
    cta: "Let's go",
  },
  {
    id: "streak",
    title: "Jourdan's Streaks",
    prompt:
      "Play **five turns in a row** without picking up a pile, and your deck catches **fire**, like yours has.\n\n" +
      "Spend the fire to lock piles on another player's next turn. Tap your deck, then tap **Sam's** deck.",
    plays: [{ move: { kind: "streak", targetId: SAM } }],
  },
  {
    id: "still-your-turn",
    title: "Then take your turn",
    prompt: (s) =>
      "The lock on Sam's deck means Sam's **two smallest piles** will be locked on Sam's next turn.\n\n" +
      `Spending the fire isn't a turn, so play yours. The **left** pile shows ${aValue(showing(s, 3))}: tap ▲.`,
    plays: [{ move: { kind: "guess", stackIndex: 3, guess: "higher" }, draws: draws("thr-yours1", [7]) }],
    debrief: "Now it's Sam's turn, and the two smallest piles are locked. Sam can't play on them.",
    cta: "Next",
  },
  {
    id: "locked-turn",
    title: "Sam's locked turn",
    prompt: "Sam has to play on one of the open piles.",
    plays: [{ by: SAM, move: { kind: "guess", stackIndex: 4, guess: "higher" }, draws: draws("thr-sam1", [5]) }],
    debrief: "Locks last for one turn, so they're gone now. Five more turns without picking up a pile, and your deck lights again.",
    cta: "Next",
  },
  {
    id: "triplet",
    title: "Your turn",
    prompt: (s) => `Your turn. The **bottom** pile shows ${aValue(showing(s, 2))}. Tap ▼ to guess Lower.`,
    plays: [
      {
        move: { kind: "guess", stackIndex: 2, guess: "lower" },
        draws: draws("thr-yours2", [9]),
        pauses: [
          {
            on: "starfall-charge",
            title: "Constellations",
            text:
              "**Three piles show a 9** on top, and that lights a **star**. This rule is **Constellations**.\n\n" +
              "One star was already lit, so that's two.",
          },
        ],
      },
    ],
  },
  {
    id: "staircase",
    title: "Your turn",
    prompt: (s) => `Your turn. The **top** pile shows ${aValue(showing(s, 1))}. Tap ▼ to guess Lower.`,
    plays: [
      { by: SAM, move: { kind: "guess", stackIndex: 4, guess: "higher" }, draws: draws("thr-sam2", [8]) },
      {
        move: { kind: "guess", stackIndex: 1, guess: "lower" },
        draws: draws("thr-yours3", [6]),
        pauses: [
          { on: "starfall-charge", title: "Four in a row", text: "**6, 7, 8 and 9** on top: four in a row lights a star too. That's three." },
          { on: "starfall-event", title: "An event", text: "**Three stars set off an event.** This one is a **Meteor Strike**." },
          { on: "meteor-strike", title: "Meteor Strike", text: "The meteors burned cards off the piles, out of the game." },
        ],
      },
    ],
    debriefTitle: "Other events",
    debrief: "Other events throw cards from a deck onto the table, or deal the whole table out again. Then the stars start over.",
    cta: "Next",
  },
  {
    id: "done",
    title: "Three more rules",
    prompt:
      "**Mercy:** end a turn holding far more cards than the rest, and some fly off your deck onto the piles.\n\n" +
      "**Drought:** take a pile three turns running, and only half of the next one comes back to you.\n\n" +
      "**Burning Deck:** an unspent fire burns a card off your deck every second turn.",
    cta: "Play Five Out",
  },
];

export const HOUSE_RULES: Tutorial = {
  id: "house-rules",
  title: "Tutorial – House Rules",
  initialState,
  steps: STEPS,
};
